import { CustomRive } from "@/components/CustomRive";
import { CheckIcon } from "@/components/icons/CheckIcon";
import { ImxRewards } from "@/components/icons/ImxRewards";
import { Fit, type Rive } from "@rive-app/react-canvas";
import { X } from "lucide-react";
import { useCallback, useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";

// Animation timing constants
const CONFETTI_DURATION_MS = 2000;
const TOAST_AUTO_CLOSE_MS = 5000;
const SLIDE_ANIMATION_MS = 300;

type CelebrationToastProps = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  title: string;
  description: string;
  gems?: number;
};

export const CelebrationToast = ({
  open,
  onOpenChange,
  title,
  description,
  gems,
}: CelebrationToastProps) => {
  const [isAnimating, setIsAnimating] = useState(false);
  const riveRef = useRef<Rive | null>(null);

  // Callback to receive rive instance from CustomRive
  const handleRiveInit = useCallback((rive: Rive | null) => {
    riveRef.current = rive;
  }, []);

  // Stop confetti animation after duration
  useEffect(() => {
    if (!open || !riveRef.current) return;

    const timer = setTimeout(() => {
      riveRef.current?.pause();
    }, CONFETTI_DURATION_MS);

    return () => clearTimeout(timer);
  }, [open]);

  // Handle slide-in animation and auto-close
  useEffect(() => {
    if (!open) {
      setIsAnimating(false);
      return;
    }

    // Trigger slide-in animation
    requestAnimationFrame(() => {
      setIsAnimating(true);
    });

    let unmountTimer: ReturnType<typeof setTimeout>;

    // Auto-close sequence
    const closeTimer = setTimeout(() => {
      setIsAnimating(false);

      // Wait for slide-out animation before unmounting
      unmountTimer = setTimeout(() => {
        onOpenChange(false);
      }, SLIDE_ANIMATION_MS);
    }, TOAST_AUTO_CLOSE_MS);

    return () => {
      clearTimeout(closeTimer);
      clearTimeout(unmountTimer);
    };
  }, [open, onOpenChange]);

  if (!open) return null;

  const toastContent = (
    <div className="fixed inset-x-0 top-0 z-50 pointer-events-none overflow-visible">
      {/* Toast Content */}
      <div
        className={`relative backdrop-blur-md bg-neutral-900/50 border-x border-b border-white/10 rounded-b-lg py-4 px-2 pointer-events-auto font-suisse transition-all duration-300 ease-out ${
          isAnimating
            ? "translate-y-0 opacity-100"
            : "-translate-y-full opacity-0"
        }`}
        style={{ backfaceVisibility: "hidden" }}
      >
        {/* Confetti Animation - Above toast content */}
        <div className="absolute inset-0 -inset-y-20 pointer-events-none overflow-visible z-20">
          <CustomRive
            src="/rive/gems-confetti.riv"
            autoplay
            fit={Fit.Cover}
            stateMachines={["State Machine 1"]}
            onRiveInit={handleRiveInit}
          />
        </div>
        {/* Close Button */}
        <button
          type="button"
          onClick={() => onOpenChange(false)}
          className="absolute top-4 right-2 rounded-full bg-white/10 hover:bg-white/20 p-1.5 transition-colors z-10"
          aria-label="Close"
        >
          <X className="size-4 text-white" />
        </button>

        {/* Title */}
        <h2 className="text-xl font-bold text-white p-3 pr-10">{title}</h2>

        <div className="flex items-center gap-3 p-3">
          <CheckIcon className="size-4 flex-shrink-0" />

          {/* Description */}
          <p className="text-xs text-[#B6B6B6] flex-grow">{description}</p>

          {/* Gems Badge */}
          {gems ? (
            <div className="rounded-md border border-status-success bg-neutral-900/20 px-1.5 py-0.5 flex items-center gap-x-1 whitespace-nowrap text-status-success">
              <span className="font-[550] text-xs">{gems}</span>
              <ImxRewards className="size-3 fill-status-success" />
            </div>
          ) : null}
        </div>
      </div>
    </div>
  );

  // Render in portal to avoid overflow constraints
  return createPortal(toastContent, document.body);
};
